import type { SeriesOptionsType } from 'highcharts';
import type Highcharts from 'highcharts';

export const LEGEND_AXIS_DIVIDER_NAME = '__axis-legend-divider__';

export type DoubleYAxisLegendAxisConfig = {
  leftAxisId: string;
  rightAxisId: string;
  defaultAxisId?: string;
};

export type DoubleYAxisLegendSeriesOption = {
  name: string;
  color?: string;
  description?: string;
  dividerBefore?: boolean;
};

const resolveAxisId = (item: { yAxis?: string }, axisConfig: DoubleYAxisLegendAxisConfig) =>
  item.yAxis ?? axisConfig.defaultAxisId ?? axisConfig.leftAxisId;

export const isLegendAxisDivider = (name?: string | null) => name === LEGEND_AXIS_DIVIDER_NAME;

export const orderDoubleYAxisSeriesForLegend = <T extends { yAxis?: string }>(
  series: T[],
  axisConfig: DoubleYAxisLegendAxisConfig,
): T[] => {
  const leftSeries = series.filter(item => resolveAxisId(item, axisConfig) === axisConfig.leftAxisId).reverse();
  const rightSeries = series.filter(item => resolveAxisId(item, axisConfig) === axisConfig.rightAxisId);
  return [...leftSeries, ...rightSeries];
};

export const buildDoubleYAxisLegendOptions = (
  series: Array<{ name?: string; color?: unknown; yAxis?: string; custom?: { description?: string } }>,
  axisConfig: DoubleYAxisLegendAxisConfig,
): DoubleYAxisLegendSeriesOption[] => {
  const orderedSeries = orderDoubleYAxisSeriesForLegend(series, axisConfig);
  const { rightAxisId, defaultAxisId = axisConfig.leftAxisId } = axisConfig;

  return orderedSeries.map((item, index) => {
    const colorCandidate = item.color;
    const previousAxisId = index > 0 ? (orderedSeries[index - 1].yAxis ?? defaultAxisId) : null;
    const currentAxisId = item.yAxis ?? defaultAxisId;
    const dividerBefore = index > 0 && previousAxisId !== rightAxisId && currentAxisId === rightAxisId;

    return {
      name: item.name ?? '',
      color: typeof colorCandidate === 'string' ? colorCandidate : undefined,
      description: item.custom?.description,
      dividerBefore,
    };
  });
};

export const createLegendAxisDividerSeries = (yAxisId?: string): SeriesOptionsType => ({
  type: 'line',
  name: LEGEND_AXIS_DIVIDER_NAME,
  className: 'double-y-axis-legend-divider-item',
  color: '#99a3af',
  data: [],
  ...(yAxisId ? { yAxis: yAxisId } : { yAxis: 0 }),
  visible: true,
  showInLegend: true,
  enableMouseTracking: false,
  lineWidth: 0,
  marker: { enabled: false },
  states: {
    inactive: { opacity: 1 },
    hover: { enabled: false },
  },
  events: {
    legendItemClick: () => false,
  },
  custom: {
    isLegendAxisDivider: true,
  },
});

type PreparedLegendSeries = {
  name?: string;
  color?: unknown;
  yAxis?: string;
  visible?: boolean;
  data?: unknown;
  custom?: {
    description?: string;
  };
};

export const appendDoubleYAxisChartSeries = (
  preparedSeries: PreparedLegendSeries[],
  axisConfig: DoubleYAxisLegendAxisConfig,
  buildSeries: (series: PreparedLegendSeries) => SeriesOptionsType,
  options?: { includeInlineLegendDivider?: boolean },
): SeriesOptionsType[] => {
  const orderedSeries = orderDoubleYAxisSeriesForLegend(preparedSeries, axisConfig);
  const { rightAxisId, defaultAxisId = axisConfig.leftAxisId } = axisConfig;
  const includeDivider = options?.includeInlineLegendDivider ?? false;
  const chartSeries: SeriesOptionsType[] = [];

  orderedSeries.forEach((series, index) => {
    const previousAxisId = index > 0 ? (orderedSeries[index - 1].yAxis ?? defaultAxisId) : null;
    const currentAxisId = series.yAxis ?? defaultAxisId;
    const isFirstRight = index > 0 && previousAxisId !== rightAxisId && currentAxisId === rightAxisId;

    if (includeDivider && isFirstRight) {
      chartSeries.push(createLegendAxisDividerSeries(axisConfig.leftAxisId));
    }

    chartSeries.push(buildSeries(series));
  });

  return chartSeries;
};

export const doubleYAxisLegendLabelFormatter = function (this: { name?: string }): string {
  if (isLegendAxisDivider(this.name)) {
    return '<span class="double-y-axis-legend-divider" aria-hidden="true"></span>';
  }
  return this.name ?? '';
};

export const hasExplicitDoubleYAxisSeriesSelection = (selectedSeriesNames: string[] | null | undefined): boolean =>
  Array.isArray(selectedSeriesNames) && selectedSeriesNames.length > 0;

export const resolveChartSeriesAxisId = (
  series: Highcharts.Series,
  chart: Highcharts.Chart,
  primaryAxisId: string,
  secondaryAxisId: string,
): string => {
  const yAxisOption = series.options?.yAxis;

  if (typeof yAxisOption === 'number') {
    const axisId = chart.yAxis?.[yAxisOption]?.options?.id;
    if (typeof axisId === 'string' && axisId.length > 0) {
      return axisId;
    }
    return yAxisOption === 1 ? secondaryAxisId : primaryAxisId;
  }

  if (typeof yAxisOption === 'string' && yAxisOption.length > 0) {
    return yAxisOption;
  }

  const linkedAxisId = series.yAxis?.options?.id;
  if (typeof linkedAxisId === 'string' && linkedAxisId.length > 0) {
    return linkedAxisId;
  }

  return primaryAxisId;
};

export type DoubleYAxisHeatmapSeriesObj = {
  name: string;
  data: number[][];
  showInNavigator: boolean;
  color?: string;
  custom?: {
    description?: string;
  };
};

type HeatmapMatrixNode = { name: string; description?: string };

type HeatmapMatrixPayload = {
  row_nodes: HeatmapMatrixNode[];
  col_nodes: HeatmapMatrixNode[];
  secondary_col_nodes?: HeatmapMatrixNode[];
  data: number[][];
  secondary_data?: number[][];
};

export const buildDoubleYAxisHeatmapSeriesData = (
  obj: HeatmapMatrixPayload,
  options: {
    parseX: (rawX: string) => number;
    resolveColor?: (seriesIndex: number, columnIndex: number, isSecondary: boolean) => string | undefined;
  },
): { seriesData1: DoubleYAxisHeatmapSeriesObj[]; seriesData2: DoubleYAxisHeatmapSeriesObj[] } => {
  const xLabels = obj.row_nodes.map(row => row.name);
  const primaryLabels = obj.col_nodes.map(column => column.name);
  const secondaryLabels = obj.secondary_col_nodes?.map(column => column.name) ?? [];
  const primaryColumnCount = primaryLabels.length;
  const matrixRowWidth = Array.isArray(obj.data[0]) ? obj.data[0].length : 0;
  const hasSecondaryMatrix = Array.isArray(obj.secondary_data) && obj.secondary_data.length > 0;
  const extraColumnsInPrimaryMatrix = Math.max(matrixRowWidth - primaryColumnCount, 0);

  const seriesData1: DoubleYAxisHeatmapSeriesObj[] = [];

  for (let j = 0; j < primaryLabels.length; ++j) {
    const columnIndex = primaryLabels.length - j - 1;
    const seriesDescription = obj.col_nodes[columnIndex]?.description;
    const seriesObj: DoubleYAxisHeatmapSeriesObj = {
      name: primaryLabels[columnIndex],
      data: [],
      showInNavigator: true,
      color: options.resolveColor?.(j, columnIndex, false),
      ...(typeof seriesDescription === 'string' && seriesDescription.trim()
        ? { custom: { description: seriesDescription } }
        : {}),
    };

    for (let i = 0; i < xLabels.length; ++i) {
      const row = obj.data[i];
      if (!Array.isArray(row)) {
        continue;
      }
      const value = row[columnIndex] as unknown as number;
      seriesObj.data.push([options.parseX(xLabels[i]), value]);
    }

    seriesData1.push(seriesObj);
  }

  const resolvedSecondaryLabels =
    secondaryLabels.length > 0
      ? secondaryLabels
      : hasSecondaryMatrix
        ? Array.from({ length: obj.secondary_data?.[0]?.length ?? 0 }, (_, index) => `Series ${index + 1}`)
        : extraColumnsInPrimaryMatrix > 0
          ? Array.from({ length: extraColumnsInPrimaryMatrix }, (_, index) => `Axis 2 ${index + 1}`)
          : [];

  const seriesData2: DoubleYAxisHeatmapSeriesObj[] = [];

  if (resolvedSecondaryLabels.length > 0) {
    for (let j = 0; j < resolvedSecondaryLabels.length; ++j) {
      const reversedIndex = resolvedSecondaryLabels.length - j - 1;
      const columnIndex = hasSecondaryMatrix ? reversedIndex : primaryColumnCount + reversedIndex;
      const dataMatrix = hasSecondaryMatrix ? obj.secondary_data! : obj.data;
      const secondaryDescription = hasSecondaryMatrix
        ? obj.secondary_col_nodes?.[reversedIndex]?.description
        : undefined;
      const seriesObj: DoubleYAxisHeatmapSeriesObj = {
        name: resolvedSecondaryLabels[reversedIndex],
        data: [],
        showInNavigator: true,
        color: options.resolveColor?.(j, columnIndex, true),
        ...(typeof secondaryDescription === 'string' && secondaryDescription.trim()
          ? { custom: { description: secondaryDescription } }
          : {}),
      };

      for (let i = 0; i < xLabels.length; ++i) {
        const row = dataMatrix[i];
        if (!Array.isArray(row)) {
          continue;
        }
        const value = row[columnIndex] as unknown as number;
        seriesObj.data.push([options.parseX(xLabels[i]), value]);
      }

      seriesData2.push(seriesObj);
    }
  }

  return { seriesData1, seriesData2 };
};

/** Inline legend axis-group separator tuning (see maintainDoubleYAxisInlineLegendDivider). */
export const INLINE_LEGEND_DIVIDER_WIDTH_TRIM = 10;
export const INLINE_LEGEND_DIVIDER_X_OFFSET = 0;

export const maintainDoubleYAxisInlineLegendDivider = (chart: Highcharts.Chart | null | undefined) => {
  if (!chart?.container) {
    return;
  }

  const legend = chart.legend as (Highcharts.Legend & { symbolWidth?: number }) | undefined;
  const symbolOffset = (legend?.symbolWidth ?? 24) + (legend?.options?.symbolPadding ?? 5);

  chart.container.querySelectorAll('.highcharts-legend-item.double-y-axis-legend-divider-item').forEach(node => {
    const item = node as HTMLElement;
    item.style.pointerEvents = 'none';
    item.style.opacity = '1';
    item.style.overflow = 'visible';

    item.querySelectorAll('.highcharts-point, :scope > path, :scope > rect').forEach(el => {
      (el as SVGElement).style.display = 'none';
    });

    const divider = item.querySelector('.double-y-axis-legend-divider') as HTMLElement | null;
    if (!divider) {
      return;
    }

    const labelElement = divider.parentElement as HTMLElement | null;
    if (labelElement) {
      labelElement.style.overflow = 'visible';
    }

    const itemWidth = item.getBoundingClientRect().width;
    const dividerWidth = Math.max(itemWidth - INLINE_LEGEND_DIVIDER_WIDTH_TRIM, 48);
    const dividerX = -(symbolOffset - INLINE_LEGEND_DIVIDER_X_OFFSET);

    divider.style.display = 'block';
    divider.style.width = `${dividerWidth}px`;
    divider.style.maxWidth = `${dividerWidth}px`;
    divider.style.marginLeft = `${dividerX}px`;
    divider.style.height = '0';
    divider.style.borderTop = '1px solid #99a3af';
    divider.style.marginTop = '8px';
    divider.style.marginBottom = '8px';
    divider.style.boxSizing = 'border-box';
    divider.style.lineHeight = '0';
    divider.style.fontSize = '0';
  });
};

/** Space between the plot edge and right y-axis tick labels (inline vertical legend). */
const INLINE_VERTICAL_RIGHT_AXIS_LABEL_GUTTER = 36;

export const resolveDoubleYAxisChartLayout = (options: {
  dropdownLegendEnabled: boolean;
  layoutSeriesCount: number;
  defaultMarginRight?: number;
  leftAxisMargin?: number;
  leftAxisOffset?: number;
}) => {
  const {
    dropdownLegendEnabled,
    layoutSeriesCount,
    defaultMarginRight = 70,
    leftAxisMargin = 30,
    leftAxisOffset = 0,
  } = options;
  const inlineLegend = !dropdownLegendEnabled && layoutSeriesCount > 1;
  const verticalInlineLegend = inlineLegend && layoutSeriesCount >= 3;

  const marginRight = dropdownLegendEnabled
    ? defaultMarginRight
    : layoutSeriesCount < 3
      ? defaultMarginRight
      : defaultMarginRight + 130;

  // Positive x shifts the vertical inline legend toward the right edge, away from y-axis labels.
  const legendX = !inlineLegend ? undefined : layoutSeriesCount < 3 ? -30 : 28;

  return {
    marginRight,
    legendX,
    verticalInlineLegend,
    spacingRight: verticalInlineLegend ? 24 : undefined,
    legendMargin: verticalInlineLegend ? 24 : undefined,
    rightAxisMargin: verticalInlineLegend ? leftAxisMargin : undefined,
    rightAxisOffset: verticalInlineLegend ? leftAxisOffset : undefined,
    rightAxisLabelAlign: verticalInlineLegend ? ('right' as const) : undefined,
    rightAxisLabelX: verticalInlineLegend ? INLINE_VERTICAL_RIGHT_AXIS_LABEL_GUTTER : undefined,
  };
};

type InitialAxisVisibilityOptions = {
  preferFirstVisible?: boolean;
  numberOfLines?: number | null;
  prism?: string;
};

export const shouldShowSeriesAtAxisIndex = (
  index: number,
  totalSeries: number,
  options: InitialAxisVisibilityOptions = {},
): boolean => {
  const { preferFirstVisible = false, numberOfLines, prism } = options;
  const parsedLimit =
    typeof numberOfLines === 'number' && Number.isFinite(numberOfLines) && numberOfLines > 0
      ? Math.floor(numberOfLines)
      : null;

  if (parsedLimit !== null) {
    if (preferFirstVisible) {
      return index < parsedLimit;
    }
    const limitStartIndex = Math.max(totalSeries - Math.min(parsedLimit, totalSeries), 0);
    return index >= limitStartIndex;
  }

  if (totalSeries > 3) {
    if (preferFirstVisible) {
      return index === 0;
    }
    if (prism === 'Central Banks' && totalSeries > 8) {
      return index === 7 || index === 8;
    }
    return index >= totalSeries - 1;
  }

  return true;
};

export const resolveInitialDoubleYAxisVisibleNames = (
  series: Array<{ name?: string; yAxis?: string }>,
  axisConfig: DoubleYAxisLegendAxisConfig,
  options: {
    numberOfLines?: number | null;
    prism?: string;
    selectedSeriesNames?: string[] | null;
  } = {},
): Set<string> => {
  const { selectedSeriesNames, numberOfLines, prism } = options;
  const parsedLimit =
    typeof numberOfLines === 'number' && Number.isFinite(numberOfLines) && numberOfLines > 0
      ? Math.floor(numberOfLines)
      : null;
  const totalChartSeries = series.reduce((count, item) => (item.name ? count + 1 : count), 0);

  if (Array.isArray(selectedSeriesNames) && selectedSeriesNames.length === 0) {
    return new Set();
  }

  if (hasExplicitDoubleYAxisSeriesSelection(selectedSeriesNames)) {
    const knownNames = new Set(series.map(item => item.name).filter((name): name is string => Boolean(name)));
    const matched = (selectedSeriesNames ?? []).filter(name => knownNames.has(name));
    if (matched.length > 0) {
      return new Set(matched);
    }
  }

  const { leftAxisId, rightAxisId, defaultAxisId = leftAxisId } = axisConfig;
  const visibleNames = new Set<string>();
  const axisNames: Record<string, string[]> = {
    [leftAxisId]: [],
    [rightAxisId]: [],
  };

  orderDoubleYAxisSeriesForLegend(series, axisConfig).forEach(item => {
    if (!item.name) {
      return;
    }
    const axisId = item.yAxis ?? defaultAxisId;
    if (axisId === rightAxisId) {
      axisNames[rightAxisId].push(item.name);
    } else {
      axisNames[leftAxisId].push(item.name);
    }
  });

  const applyAxis = (names: string[], preferFirstVisible: boolean) => {
    const totalSeries = names.length;
    names.forEach((name, index) => {
      let shouldShow: boolean;

      if (parsedLimit !== null) {
        shouldShow = shouldShowSeriesAtAxisIndex(index, totalSeries, { preferFirstVisible, numberOfLines, prism });
      } else if (totalChartSeries > 2) {
        shouldShow = preferFirstVisible ? index === 0 : index === totalSeries - 1;
      } else {
        shouldShow = shouldShowSeriesAtAxisIndex(index, totalSeries, { preferFirstVisible, numberOfLines, prism });
      }

      if (shouldShow) {
        visibleNames.add(name);
      }
    });
  };

  applyAxis(axisNames[leftAxisId], true);
  applyAxis(axisNames[rightAxisId], false);

  return visibleNames;
};

export const applyInitialAxisSeriesVisibility = (
  axisSeries: Array<{ setVisible?: (visible: boolean, redraw?: boolean) => void }>,
  options: InitialAxisVisibilityOptions = {},
) => {
  const totalSeries = axisSeries.length;

  axisSeries.forEach((series, index) => {
    if (!series?.setVisible) return;
    series.setVisible(shouldShowSeriesAtAxisIndex(index, totalSeries, options), false);
  });
};
